import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "../../../components/home/Nav";
import Header from "../../../components/home/Header";
import { setUserToken } from "../../../store/reducers/authReducer";
import FieldError from "../../../components/ui/FieldError";
import { getApiErrors, getFieldError, getFriendlyApiError } from "../../../utils/formErrors";
import "../../../styles/form-errors.css";
import {
  useLoginWithPasswordMutation,
  useRequestOtpMutation,
  useVerifyOtpMutation,
} from "../../../store/services/authService";

const OTP_LENGTH = 4;
const RESEND_SECONDS = 60;

const normalizeDigits = (value = "") =>
  value
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)))
    .replace(/\D/g, "");

const toFaDigits = (value = "") =>
  String(value).replace(/[0-9]/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);

const formatMobile = (value) => toFaDigits(normalizeDigits(value));


const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const otpRefs = useRef([]);
  const lastSubmittedOtpRef = useRef("");

  const redirectTo =
    location.state?.from ||
    sessionStorage.getItem("redirectAfterLogin") ||
    "/user";

  const [mode, setMode] = useState("otp");
  const [step, setStep] = useState("mobile");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [testOtp, setTestOtp] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [resendIn, setResendIn] = useState(0);

  const otp = otpDigits.join("");

  const [requestOtp, requestResponse] = useRequestOtpMutation();
  const [verifyOtp, verifyResponse] = useVerifyOtpMutation();
  const [loginWithPassword, passwordLoginResponse] = useLoginWithPasswordMutation();

  const cleanMobile = normalizeDigits(mobile);
  const isValidMobile = /^09\d{9}$/.test(cleanMobile);

  const completeLogin = useCallback((res) => {
    dispatch(
      setUserToken({
        token: res.token,
        user: res.user,
      })
    );

    sessionStorage.removeItem("redirectAfterLogin");
    navigate(redirectTo, { replace: true });
  }, [dispatch, navigate, redirectTo]);

  const startResendTimer = (seconds = RESEND_SECONDS) => {
    const value = Number(seconds);

    setResendIn(
      Number.isFinite(value) && value > 0
        ? Math.ceil(value)
        : RESEND_SECONDS
    );
  };

  const submitMobile = async (e) => {
    e?.preventDefault?.();
    setError("");
    setFieldErrors({});

    if (!isValidMobile) {
      setError("موبایل درست نیست.");
      setFieldErrors({ mobile: ["شماره را با 09 وارد کنید."] });
      return;
    }

    try {
      const res = await requestOtp({ mobile: cleanMobile }).unwrap();
      setMobile(res.mobile || cleanMobile);
      setTestOtp(res.test_otp || "");
      setOtpDigits(Array(OTP_LENGTH).fill(""));
      lastSubmittedOtpRef.current = "";
      setStep("otp");
      startResendTimer(res?.resend_in || RESEND_SECONDS);
    } catch (err) {
      const errors = getApiErrors(err);
      setFieldErrors(errors);
      setError(getFriendlyApiError(err, "کد ارسال نشد."));
    }
  };

  const resendOtp = async () => {
    if (resendIn > 0 || requestResponse.isLoading) return;

    setError("");
    setFieldErrors({});

    if (!isValidMobile) {
      setError("موبایل درست نیست.");
      setFieldErrors({ mobile: ["موبایل را دوباره وارد کنید."] });
      setStep("mobile");
      return;
    }

    try {
      const res = await requestOtp({ mobile: cleanMobile }).unwrap();
      setMobile(res.mobile || cleanMobile);
      setTestOtp(res.test_otp || "");
      setOtpDigits(Array(OTP_LENGTH).fill(""));
      lastSubmittedOtpRef.current = "";
      startResendTimer(res?.resend_in || RESEND_SECONDS);
      setTimeout(() => otpRefs.current[0]?.focus(), 120);
    } catch (err) {
      const errors = getApiErrors(err);
      setFieldErrors(errors);
      setError(getFriendlyApiError(err, "کد دوباره ارسال نشد."));
    }
  };

  const submitPasswordLogin = async (e) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    if (!isValidMobile) {
      setError("موبایل درست نیست.");
      setFieldErrors({ mobile: ["شماره را با 09 وارد کنید."] });
      return;
    }

    if (!password.trim()) {
      setError("رمز را وارد کنید.");
      setFieldErrors({ password: ["رمز را وارد کنید."] });
      return;
    }

    try {
      const res = await loginWithPassword({ mobile: cleanMobile, password }).unwrap();
      completeLogin(res);
    } catch (err) {
      const errors = getApiErrors(err);
      setFieldErrors(errors);
      setError(getFriendlyApiError(err, "موبایل یا رمز درست نیست."));
    }
  };

  const verifyOtpCode = useCallback(async (code) => {
    if (verifyResponse.isLoading) return;

    if (lastSubmittedOtpRef.current === code) return;

    setError("");
    setFieldErrors({});

    if (code.length < OTP_LENGTH) {
      setError("کد کامل نیست.");
      setFieldErrors({ otp: ["کد ۴ رقمی را کامل کنید."] });
      return;
    }

    lastSubmittedOtpRef.current = code;

    try {
      const res = await verifyOtp({ mobile: cleanMobile, otp: code }).unwrap();
      completeLogin(res);
    } catch (err) {
      const errors = getApiErrors(err);
      setFieldErrors(errors);
      setError(getFriendlyApiError(err, "کد درست نیست."));

      setOtpDigits(Array(OTP_LENGTH).fill(""));
      setTimeout(() => otpRefs.current[0]?.focus(), 120);
    }
  }, [cleanMobile, completeLogin, verifyOtp, verifyResponse.isLoading]);

  const submitOtp = async (e) => {
    e.preventDefault();
    await verifyOtpCode(otp);
  };

  const setOtpFromString = (value, startIndex = 0) => {
    lastSubmittedOtpRef.current = "";
    setError("");
    setFieldErrors({});

    const digits = normalizeDigits(value).slice(0, OTP_LENGTH - startIndex).split("");

    if (!digits.length) return;

    setOtpDigits((prev) => {
      const next = [...prev];

      digits.forEach((digit, index) => {
        next[startIndex + index] = digit;
      });

      return next;
    });

    const nextFocus = Math.min(startIndex + digits.length, OTP_LENGTH - 1);
    setTimeout(() => otpRefs.current[nextFocus]?.focus(), 0);
  };

  const handleOtpChange = (index, value) => {
    lastSubmittedOtpRef.current = "";
    setError("");
    setFieldErrors({});

    const cleanValue = normalizeDigits(value);

    if (cleanValue.length > 1) {
      setOtpFromString(cleanValue, index);
      return;
    }

    setOtpDigits((prev) => {
      const next = [...prev];
      next[index] = cleanValue;
      return next;
    });

    if (cleanValue && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }

    if (e.key === "ArrowLeft" && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }

    if (e.key === "ArrowRight" && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (index, e) => {
    e.preventDefault();
    setOtpFromString(e.clipboardData.getData("text"), index);
  };

  const switchToOtp = () => {
    setMode("otp");
    setStep("mobile");
    setPassword("");
    setError("");
    setFieldErrors({});
  };

  const switchToPassword = () => {
    setMode("password");
    setStep("mobile");
    setOtpDigits(Array(OTP_LENGTH).fill(""));
    setTestOtp("");
    setError("");
    setFieldErrors({});
  };

  const changeMobile = () => {
    setStep("mobile");
    setOtpDigits(Array(OTP_LENGTH).fill(""));
    lastSubmittedOtpRef.current = "";
    setTestOtp("");
    setError("");
    setFieldErrors({});
    setResendIn(0);
  };

  useEffect(() => {
    if (step === "otp") {
      setTimeout(() => otpRefs.current[0]?.focus(), 120);
    }
  }, [step]);

  useEffect(() => {
    if (mode === "otp" && step === "otp" && otp.length === OTP_LENGTH && !verifyResponse.isLoading) {
      verifyOtpCode(otp);
    }
  }, [otp, mode, step, verifyOtpCode, verifyResponse.isLoading]);

  useEffect(() => {
    if (verifyResponse.isSuccess) {
      setOtpDigits(Array(OTP_LENGTH).fill(""));
    }
  }, [verifyResponse.isSuccess]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;

    const timer = setInterval(() => {
      setResendIn((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendIn]);

  return (
    <>
      <Nav />

      <div className="auth-page-shell">
        <Header>ورود</Header>

        <div className="auth-layout">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="auth-panel"
          >
            <div className="auth-card-modern">
              <div className="auth-card-head">
                <h1>
                  {step === "otp"
                    ? "کد تایید"
                    : mode === "password"
                      ? "ورود با رمز"
                      : "ورود سریع"}
                </h1>

                <p>
                  {step === "otp"
                    ? "کد پیامک‌شده را وارد کنید."
                    : "شماره موبایل را وارد کنید."}
                </p>
              </div>

              <div className="auth-method-switch">
                <button
                  type="button"
                  className={mode === "password" ? "active" : ""}
                  onClick={switchToPassword}
                >
                  ورود با رمز
                </button>

                <button
                  type="button"
                  className={mode === "otp" ? "active" : ""}
                  onClick={switchToOtp}
                >
                  کد پیامکی
                </button>
              </div>

              {error && <div className="alert-danger auth-alert">{error}</div>}

              {testOtp && step === "otp" && (
                <div className="auth-test-code">
                  کد تست فعلی: <strong>{testOtp}</strong>
                </div>
              )}

              {mode === "password" && (
                <form onSubmit={submitPasswordLogin} className="auth-form">
                  <div className={`auth-field ${getFieldError(fieldErrors, "mobile") ? "has-field-error" : ""}`}>
                    <label>شماره موبایل</label>
                    <div className="auth-input-wrap ltr">
                      <input
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel"
                        placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                        value={toFaDigits(mobile)}
                        onChange={(e) => {
                          setMobile(normalizeDigits(e.target.value).slice(0, 11));
                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.mobile;
                            return next;
                          });
                        }}
                      />
                    </div>
                    <FieldError message={getFieldError(fieldErrors, "mobile")} />
                  </div>

                  <div className={`auth-field ${getFieldError(fieldErrors, "password") ? "has-field-error" : ""}`}>
                    <label>رمز عبور</label>
                    <div className="auth-input-wrap">
                      <input
                        type="password"
                        autoComplete="current-password"
                        placeholder="رمز عبور حساب"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.password;
                            return next;
                          });
                        }}
                      />
                    </div>
                    <FieldError message={getFieldError(fieldErrors, "password")} />
                  </div>

                  <button
                    type="submit"
                    className="auth-primary-action"
                    disabled={passwordLoginResponse.isLoading}
                  >
                    {passwordLoginResponse.isLoading ? "ورود..." : "ورود با رمز"}
                  </button>

                  <div className="auth-inline-switch">
                    <span></span>
                    <button type="button" onClick={switchToOtp}>
                      ورود با کد پیامکی
                    </button>
                  </div>
                </form>
              )}

              {mode === "otp" && step === "mobile" && (
                <form onSubmit={submitMobile} className="auth-form">
                  <div className={`auth-field ${getFieldError(fieldErrors, "mobile") ? "has-field-error" : ""}`}>
                    <label>شماره موبایل</label>
                    <div className="auth-input-wrap ltr">
                      <input
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel"
                        placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                        value={toFaDigits(mobile)}
                        onChange={(e) => {
                          setMobile(normalizeDigits(e.target.value).slice(0, 11));
                          setFieldErrors((prev) => {
                            const next = { ...prev };
                            delete next.mobile;
                            return next;
                          });
                        }}
                      />
                    </div>
                    <FieldError message={getFieldError(fieldErrors, "mobile")} />
                  </div>

                  <button
                    type="submit"
                    className="auth-primary-action"
                    disabled={requestResponse.isLoading}
                  >
                    {requestResponse.isLoading ? "ارسال..." : "دریافت کد"}
                  </button>

                  <div className="auth-inline-switch">
                    <span></span>
                    <button type="button" onClick={switchToPassword}>
                      ورود با رمز
                    </button>
                  </div>
                </form>
              )}

              {mode === "otp" && step === "otp" && (
                <form onSubmit={submitOtp} className="auth-form">
                  <div className="auth-otp-mobile-box">
                    <span>کد تایید برای این شماره ارسال شد</span>
                    <button type="button" onClick={changeMobile}>
                      {formatMobile(mobile)}
                      <small>{resendIn > 0 ? `تغییر پس از ${toFaDigits(resendIn)} ثانیه` : "تغییر شماره"}</small>
                    </button>
                  </div>

                  <div className={`auth-field ${getFieldError(fieldErrors, "otp") ? "has-field-error" : ""}`}>
                    <label>کد تایید</label>

                    <div className={`otp-input-group ${getFieldError(fieldErrors, "otp") ? "otp-has-error" : ""}`} dir="ltr">
                      {otpDigits.map((digit, index) => (
                        <input
                          key={index}
                          ref={(el) => {
                            otpRefs.current[index] = el;
                          }}
                          type="text"
                          inputMode="numeric"
                          autoComplete={index === 0 ? "one-time-code" : "off"}
                          maxLength={OTP_LENGTH}
                          className="otp-input"
                          disabled={verifyResponse.isLoading}
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          onPaste={(e) => handleOtpPaste(index, e)}
                        />
                      ))}
                    </div>

                    <FieldError message={getFieldError(fieldErrors, "otp")} />


                  </div>

                  <button
                    type="submit"
                    className="auth-primary-action"
                    disabled={verifyResponse.isLoading}
                  >
                    {verifyResponse.isLoading ? "بررسی..." : "ادامه خرید"}
                  </button>

                  <button
                    type="button"
                    className={`auth-resend-btn ${resendIn === 0 ? "ready" : ""}`}
                    onClick={resendOtp}
                    disabled={resendIn > 0 || requestResponse.isLoading}
                  >
                    {requestResponse.isLoading
                      ? "ارسال..."
                      : resendIn > 0
                        ? `ارسال مجدد تا ${toFaDigits(resendIn)} ثانیه دیگر`
                        : "ارسال مجدد کد"}
                  </button>
                </form>
              )}

              <div className="auth-card-footer">
                <Link to="/">صفحه اصلی</Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Login;
